import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DEFAULT_PACING_PCT } from '@classroom-ai/pacing';
import { useGetCoursePacingTarget, useSetCoursePacingTarget } from '@dataconnect/generated/react';

import { invalidateAll } from 'src/lib/invalidate-queries';

// ----------------------------------------------------------------------
// A course's saved Pacing utilization target (epic #266, Phase B). One value per
// course, because lesson content is shared course-wide: the Pacing tab starts from
// it, and the "Plan with AI" wizard sizes a unit's lesson budget with it, so a
// generated unit fits the same calendar the teacher sees. Null in the DB → 75%.


export function useCoursePacingTarget(courseId: string | null | undefined) {
  const queryClient = useQueryClient();
  const q = useGetCoursePacingTarget({ courseId: courseId ?? '' }, { enabled: !!courseId });
  const { mutateAsync, isPending: saving } = useSetCoursePacingTarget();

  const saved = q.data?.course?.pacingTargetPct ?? null;

  const save = useCallback(
    async (pct: number) => {
      if (!courseId) return;
      await mutateAsync({ courseId, pct });
      await invalidateAll(queryClient);
    },
    [courseId, mutateAsync, queryClient]
  );

  return {
    /** The effective target (%): the saved value, or the 75% default. */
    pct: saved ?? DEFAULT_PACING_PCT,
    /** True once the course row has loaded (so callers don't flash the default). */
    isLoaded: !courseId || !q.isPending,
    save,
    saving,
  };
}
